// Activity heartbeat: records that an authenticated user opened the app, so we
// can track real "last active" time (including persisted-session opens that
// never re-trigger a sign-in). The write is throttled per-user so a busy
// session doesn't hammer Firestore.

const DEFAULT_WINDOW_MS = 60 * 60 * 1000; // 1 hour

function storageKey(userId) {
  return `lastActivityWrite:${userId}`;
}

/**
 * Pure throttle decision: should we record an activity heartbeat now?
 * @param {number|null} lastWriteMs - epoch ms of the last recorded heartbeat, or null if never
 * @param {number} nowMs - current epoch ms
 * @param {number} windowMs - minimum gap between heartbeats
 */
export function shouldWriteActivity(lastWriteMs, nowMs, windowMs) {
  if (lastWriteMs == null) return true;
  return nowMs - lastWriteMs >= windowMs;
}

/**
 * Record an activity heartbeat for a user if the throttle window has elapsed.
 * Best-effort: a failed write does NOT advance the throttle, so the next app
 * open retries. Dependencies are injected for testability.
 *
 * @param {string} userId
 * @param {object} [opts]
 * @param {number} [opts.now] - current epoch ms (defaults to Date.now())
 * @param {number} [opts.windowMs] - throttle window (defaults to 1 hour)
 * @param {{getItem: Function, setItem: Function}} [opts.storage] - defaults to localStorage
 * @param {(userId: string) => Promise<void>} opts.write - performs the Firestore write
 * @returns {Promise<boolean>} whether a write was performed
 */
export async function recordActivityHeartbeat(userId, opts = {}) {
  const {
    now = Date.now(),
    windowMs = DEFAULT_WINDOW_MS,
    storage = typeof localStorage !== 'undefined' ? localStorage : null,
    write,
  } = opts;

  if (!userId || !write || !storage) return false;

  const key = storageKey(userId);
  const raw = storage.getItem(key);
  const lastWriteMs = raw == null ? null : Number(raw);

  if (!shouldWriteActivity(lastWriteMs, now, windowMs)) return false;

  try {
    await write(userId);
  } catch (error) {
    // Best-effort: never block the app, and leave the throttle un-advanced so
    // the next open retries.
    console.warn('Activity heartbeat write failed (will retry):', error);
    return false;
  }

  storage.setItem(key, String(now));
  return true;
}
